import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  Radio,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import EmailListService from '../../../services/EmailListService';

const CreateListModal = ({ open, onClose, onSave, allLists = [] }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedListData, setSelectedListData] = useState(null); // Datos completos de la lista seleccionada
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  
  // Estados para el paso 2 (clientes)
  const [clients, setClients] = useState([]);
  const [b2bClients, setB2bClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientsFullData, setSelectedClientsFullData] = useState([]); // Datos completos de clientes seleccionados
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('all'); // 'all', 'b2c', 'b2b'
  const [clientPaginationModel, setClientPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [clientLoading, setClientLoading] = useState(false);
  const [clientTotalRows, setClientTotalRows] = useState(0);
  const [loadingListContacts, setLoadingListContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isUpdatingSelectionRef = useRef(false);

  // Separar listas del sistema y personalizadas
  const systemLists = React.useMemo(() => {
    return allLists.filter(l => l.isSystem === true);
  }, [allLists]);

  const customLists = React.useMemo(() => {
    return allLists.filter(l => !l.isSystem);
  }, [allLists]);

  // Combinar todas las listas para mostrar en tabla
  const allListOptions = React.useMemo(() => {
    const startFromScratchOption = {
      id: 'start_from_scratch',
      name: t('start_from_scratch') || 'Empezar desde cero',
      description: t('create_new_list_from_scratch') || 'Crear una nueva lista sin clonar',
      isSystem: false,
      isStartFromScratch: true
    };
    return [startFromScratchOption, ...systemLists, ...customLists];
  }, [systemLists, customLists, t]);

  // Resetear cuando se abre el modal
  useEffect(() => {
    if (open) {
      console.log('[CreateListModal] Modal opened');
      setActiveStep(0);
      setSelectedListId(null);
      setSelectedListData(null);
      setFormData({
        name: '',
        description: ''
      });
      setSelectedClients([]);
      setSelectedClientsFullData([]);
      setClientSearchTerm('');
      setClientTypeFilter('all');
      setClientPaginationModel({ page: 0, pageSize: 10 });
    } else {
      console.log('[CreateListModal] Modal closed');
    }
  }, [open]);

  // Función para mapear contacts a formato de clientes
  const mapContactsToClients = (contacts) => {
    return contacts
      .filter(contact => contact.id && contact.id !== '-' && contact.id !== 'null' && String(contact.id).toLowerCase() !== 'null')
      .map((contact) => {
        const clientType = contact.type || 'B2C';
        const clientId = String(contact.id).trim();
        
        return {
          id: clientType === 'B2B' ? `b2b_${clientId}` : `b2c_${clientId}`,
          clientId: clientId,
          clientType: clientType,
          displayName: contact.name || `Cliente ${clientId}`,
          identifier: clientId,
          email: contact.email,
          ...contact
        };
      });
  };

  // Cuando se selecciona una lista, solo actualizar el formulario con el nombre
  useEffect(() => {
    if (selectedListId && selectedListId !== 'start_from_scratch') {
      const selectedList = allLists.find(l => 
        l.id === selectedListId || String(l.id) === String(selectedListId)
      );
      if (selectedList) {
        setFormData({
          name: `${selectedList.name} (Copia)`,
          description: selectedList.description || ''
        });
        // Guardar datos básicos de la lista seleccionada
        setSelectedListData({
          id: selectedList.id,
          name: selectedList.name,
          isSystem: selectedList.isSystem,
          filters: selectedList.filters
        });
      }
    } else if (selectedListId === 'start_from_scratch') {
      console.log('[CreateListModal] Starting from scratch selected');
      setSelectedListData(null);
      // Solo actualizar el formulario si está vacío, para no sobrescribir lo que el usuario ya ingresó
      setFormData(prev => {
        if (prev.name === '' && prev.description === '') {
          return { name: '', description: '' };
        }
        return prev;
      });
      // NO limpiar selectedClients ni selectedClientsFullData aquí
      // La limpieza inicial cuando se abre el modal es suficiente
      // Limpiar aquí causaría que se borren los clientes cuando el usuario ya los ha seleccionado
    }
  }, [selectedListId, allLists]);

  // Función unificada para cargar clientes (B2C y B2B) usando EmailListService
  const loadClients = useCallback(async (page, pageSize, search, type) => {
    try {
      setClientLoading(true);
      console.log('[CreateListModal] Loading clients:', { page, pageSize, search, type });
      
      // Usar EmailListService para obtener clientes
      const response = await EmailListService.getClients(page, pageSize, search, type);
      
      const clientsData = response?.clients || [];
      const total = response?.total || 0;
      
      // Separar B2C y B2B según el campo type del cliente
      // Si el filtro es 'all', traer todos; si es 'b2c' o 'b2b', solo ese tipo
      const b2cClients = clientsData.filter(c => {
        const clientType = c.type || (c.ci ? 'B2C' : null);
        return (!type || type === 'all' || type === 'b2c') && (clientType === 'B2C' || (!c.type && c.ci));
      });
      
      const b2bClients = clientsData.filter(c => {
        const clientType = c.type || (c.rut ? 'B2B' : null);
        return (!type || type === 'all' || type === 'b2b') && (clientType === 'B2B' || (!c.type && c.rut));
      });
      
      setClients(Array.isArray(b2cClients) ? b2cClients : []);
      setB2bClients(Array.isArray(b2bClients) ? b2bClients : []);
      setClientTotalRows(total);
      
      console.log('[CreateListModal] Clients loaded:', { 
        total: clientsData.length, 
        b2c: b2cClients.length, 
        b2b: b2bClients.length,
        totalRows: total
      });
    } catch (error) {
      console.error('[CreateListModal] Error loading clients:', error);
      setClients([]);
      setB2bClients([]);
      setClientTotalRows(0);
    } finally {
      setClientLoading(false);
    }
  }, []);

  // Cargar contacts de la lista cuando se entra al paso 3 (si se está clonando)
  useEffect(() => {
    const loadListContacts = async () => {
      if (activeStep === 2 && selectedListId && selectedListId !== 'start_from_scratch') {
        try {
          setLoadingListContacts(true);
          const listId = isNaN(selectedListId) ? selectedListId : parseInt(selectedListId);
          console.log('[CreateListModal] Loading list contacts from backend (step 2):', listId);
          
          // Hacer GET de la lista completa del backend para obtener los contacts
          const listData = await EmailListService.getListById(listId);
          
          console.log('[CreateListModal] List data loaded:', {
            id: listData?.id,
            name: listData?.name,
            isSystem: listData?.isSystem,
            filters: listData?.filters,
            contactsCount: listData?.contacts?.length || 0
          });
          
          // Actualizar los datos completos de la lista
          setSelectedListData(listData);
          
          // Actualizar el formulario con los datos de la lista
          setFormData(prev => ({
            ...prev,
            name: `${listData.name} (Copia)`,
            description: listData.description || prev.description
          }));
          
          // Precargar TODOS los clientes si vienen en la respuesta
          const listContacts = listData?.contacts || [];
          if (listContacts.length > 0) {
            console.log('[CreateListModal] Precargando TODOS los clientes de la lista seleccionada:', listContacts.length);
            const preloadedClients = mapContactsToClients(listContacts);
            const selectedIds = preloadedClients.map(c => c.id);
            console.log('[CreateListModal] IDs de clientes precargados:', selectedIds.slice(0, 5), '... (total:', selectedIds.length, ')');
            
            // Usar el flag para evitar que el DataGrid interfiera
            isUpdatingSelectionRef.current = true;
            setSelectedClients(selectedIds);
            setSelectedClientsFullData(preloadedClients);
            console.log('[CreateListModal] Clientes precargados - selectedClients:', selectedIds.length, 'selectedClientsFullData:', preloadedClients.length);
            
            // Resetear el flag después de un delay
            setTimeout(() => {
              isUpdatingSelectionRef.current = false;
            }, 200);
          } else {
            console.log('[CreateListModal] No hay contacts en la respuesta, limpiando selección');
            setSelectedClients([]);
            setSelectedClientsFullData([]);
          }
        } catch (error) {
          console.error('[CreateListModal] Error loading list contacts:', error);
          // Si falla, limpiar la selección
          setSelectedClients([]);
          setSelectedClientsFullData([]);
        } finally {
          setLoadingListContacts(false);
        }
      } else if (activeStep === 2 && selectedListId === 'start_from_scratch') {
        // Si es empezar desde cero, no hacer nada aquí
        // La limpieza ya se hizo cuando se seleccionó "start_from_scratch" en el useEffect anterior
        // No limpiar aquí para evitar borrar la selección cuando el usuario ya ha agregado clientes
        setLoadingListContacts(false);
      }
    };
    
    loadListContacts();
  }, [activeStep, selectedListId]);

  // Debounce para búsqueda de clientes - solo se ejecuta cuando cambia el término de búsqueda
  useEffect(() => {
    if (activeStep === 2 && open) {
      // Limpiar timeout anterior si existe
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Resetear a página 0 cuando cambia el término de búsqueda
      if (clientPaginationModel.page !== 0) {
        setClientPaginationModel(prev => ({ ...prev, page: 0 }));
      }
      
      // Crear nuevo timeout con debounce de 500ms
      searchTimeoutRef.current = setTimeout(() => {
        loadClients(1, clientPaginationModel.pageSize, clientSearchTerm, clientTypeFilter);
        searchTimeoutRef.current = null; // Limpiar después de ejecutar
      }, 500);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [clientSearchTerm, activeStep, open, clientPaginationModel.pageSize, clientTypeFilter, loadClients]);

  // Cargar clientes cuando se está en el paso 3 (para la tabla) - unificado
  // Carga inmediata para cambios de página, tamaño de página y tipo de filtro
  useEffect(() => {
    if (activeStep === 2 && open && !searchTimeoutRef.current) {
      loadClients(
        clientPaginationModel.page + 1, 
        clientPaginationModel.pageSize, 
        clientSearchTerm, 
        clientTypeFilter
      );
    }
  }, [activeStep, open, clientPaginationModel.page, clientPaginationModel.pageSize, clientTypeFilter, loadClients, clientSearchTerm]);

  // Combinar clientes según el filtro - mapear desde los datos unificados
  const displayClients = React.useMemo(() => {
    let combinedClients = [];
    
    // Mapear clientes B2C
    if (clientTypeFilter === 'all' || clientTypeFilter === 'b2c') {
      const b2cClientsMapped = clients.map(client => {
        const clientId = client.ci || client.id || client.clientId;
        return {
          ...client,
          id: `b2c_${clientId}`,
          clientType: 'B2C',
          displayName: client.displayName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || `Cliente ${clientId}`,
          clientId: clientId,
          identifier: clientId,
          email: client.email || client.Email || client.emailAddress || client.mail || null
        };
      });
      combinedClients = [...combinedClients, ...b2cClientsMapped];
    }
    
    // Mapear clientes B2B
    if (clientTypeFilter === 'all' || clientTypeFilter === 'b2b') {
      const b2bClientsMapped = b2bClients.map(client => {
        const clientId = client.rut || client.id || client.clientId;
        return {
          ...client,
          id: `b2b_${clientId}`,
          clientType: 'B2B',
          displayName: client.displayName || client.razonSocial || client.nombreFantasia || client.contacto || client.name || `Cliente ${clientId}`,
          clientId: clientId,
          identifier: clientId,
          email: client.email || client.Email || client.emailAddress || client.mail || client.contactoEmail || null
        };
      });
      combinedClients = [...combinedClients, ...b2bClientsMapped];
    }
    
    return combinedClients;
  }, [clients, b2bClients, clientTypeFilter]);


  // Función para eliminar un cliente de la selección
  const handleRemoveClient = (clientId) => {
    setSelectedClients(prev => prev.filter(id => id !== clientId));
    setSelectedClientsFullData(prev => prev.filter(c => c.id !== clientId));
  };

  // Actualizar datos completos cuando se selecciona/deselecciona en la tabla (paso 2 y 3)
  const handleClientSelectionChange = (newSelection) => {
    // Evitar loop infinito: si ya estamos actualizando, ignorar
    if (isUpdatingSelectionRef.current) {
      console.log('[CreateListModal] Update in progress, skipping');
      return;
    }
    
    console.log('[CreateListModal] Clients selected in table:', newSelection.length);
    console.log('[CreateListModal] Current selectedClients:', selectedClients.length);
    console.log('[CreateListModal] Current selectedClientsFullData:', selectedClientsFullData.length);
    
    // Combinar la selección de la tabla con los clientes precargados que no están en la tabla
    // Mantener todos los clientes que están en selectedClientsFullData pero no en displayClients (precargados)
    const preloadedClientsNotInTable = selectedClientsFullData.filter(
      c => !displayClients.some(dc => dc.id === c.id)
    );
    
    // Mantener los clientes que están tanto en selectedClientsFullData como en la nueva selección
    const existingClientsInTable = selectedClientsFullData.filter(
      c => newSelection.includes(c.id) && displayClients.some(dc => dc.id === c.id)
    );
    
    // Agregar los nuevos clientes seleccionados de la tabla que no están en selectedClientsFullData
    const newlySelected = newSelection.filter(id => !selectedClientsFullData.some(c => c.id === id));
    const newClientsData = newlySelected.map(clientId => {
      const client = displayClients.find(c => c.id === clientId);
      if (client) {
        return {
          id: client.id,
          clientId: client.identifier || client.clientId,
          clientType: client.clientType,
          displayName: client.displayName || client.name || `Cliente ${client.identifier || client.clientId}`,
          identifier: client.identifier || client.clientId,
          email: client.email,
          ...client
        };
      }
      return null;
    }).filter(Boolean);
    
    // Combinar: precargados no en tabla + existentes en tabla + nuevos de la tabla
    const combinedSelection = [...preloadedClientsNotInTable, ...existingClientsInTable, ...newClientsData];
    const combinedIds = combinedSelection.map(c => c.id);
    
    // Verificar si realmente hay cambios antes de actualizar
    const currentIds = selectedClients;
    const idsChanged = currentIds.length !== combinedIds.length || 
                       !currentIds.every(id => combinedIds.includes(id)) ||
                       !combinedIds.every(id => currentIds.includes(id));
    
    console.log('[CreateListModal] Selection change check:', {
      currentIdsLength: currentIds.length,
      combinedIdsLength: combinedIds.length,
      idsChanged,
      preloadedNotInTable: preloadedClientsNotInTable.length,
      existingInTable: existingClientsInTable.length,
      newFromTable: newClientsData.length,
      totalCombined: combinedSelection.length
    });
    
    if (!idsChanged) {
      console.log('[CreateListModal] No changes detected, skipping update');
      return;
    }
    
    console.log('[CreateListModal] Updating selection - preloaded not in table:', preloadedClientsNotInTable.length, 
                'existing in table:', existingClientsInTable.length, 
                'new from table:', newClientsData.length,
                'total:', combinedSelection.length);
    
    isUpdatingSelectionRef.current = true;
    setSelectedClients(combinedIds);
    setSelectedClientsFullData(combinedSelection);
    
    // Resetear el flag después de un pequeño delay para permitir que React actualice
    setTimeout(() => {
      isUpdatingSelectionRef.current = false;
      console.log('[CreateListModal] Update flag reset, new selectedClientsFullData length:', combinedSelection.length);
    }, 100);
  };

  const handleNext = () => {
    console.log('[CreateListModal] Moving to next step:', {
      currentStep: activeStep,
      nextStep: activeStep + 1
    });
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    console.log('[CreateListModal] Moving to previous step:', {
      currentStep: activeStep,
      previousStep: activeStep - 1
    });
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Prevenir doble llamada
    if (saving) {
      console.warn('[CreateListModal] Save already in progress, ignoring duplicate call');
      return;
    }
    
    if (!formData.name.trim()) {
      console.warn('[CreateListModal] Cannot save: name is empty');
      return;
    }
    
    if (selectedClientsFullData.length === 0) {
      console.warn('[CreateListModal] Cannot save: no clients selected');
      return;
    }
    
    setSaving(true);
    
    try {
      // Obtener los emails de los clientes seleccionados
      // Buscar el email en diferentes campos posibles
      const clientEmails = selectedClientsFullData
        .map(client => {
          // Intentar obtener el email de diferentes campos
          return client.email || client.Email || client.emailAddress || client.mail || null;
        })
        .filter(email => email && typeof email === 'string' && email.trim() !== '' && email.trim() !== 'null' && email.trim() !== '-' && email.includes('@'));
      
      console.log('[CreateListModal] Saving list - Debug:', {
        totalClientsSelected: selectedClientsFullData.length,
        clientsWithEmail: selectedClientsFullData.filter(c => c.email || c.Email || c.emailAddress || c.mail).length,
        sampleClient: selectedClientsFullData[0] ? {
          id: selectedClientsFullData[0].id,
          email: selectedClientsFullData[0].email,
          Email: selectedClientsFullData[0].Email,
          emailAddress: selectedClientsFullData[0].emailAddress,
          allKeys: Object.keys(selectedClientsFullData[0])
        } : null
      });
      
      console.log('[CreateListModal] Saving list - Request:', {
        name: formData.name,
        description: formData.description || '',
        emailsCount: clientEmails.length,
        totalClientsSelected: selectedClientsFullData.length,
        emails: clientEmails.slice(0, 5) // Mostrar primeros 5 emails para debug
      });
      
      // Preparar los datos para crear la lista
      const listData = {
        name: formData.name.trim(),
        description: formData.description || '',
        emails: clientEmails
      };
      
      console.log('[CreateListModal] List data prepared, calling onSave');
      
      // Llamar a onSave con los datos para que el componente padre maneje la creación
      if (onSave) {
        await onSave(listData);
      }
      
      // Limpiar el formulario
      setFormData({ name: '', description: '' });
      setSelectedClients([]);
      setSelectedClientsFullData([]);
      setActiveStep(0);
      setSelectedListId(null);
      onClose();
    } catch (error) {
      console.error('[CreateListModal] Error creating list:', error);
      // El error ya se maneja en EmailListService, aquí solo logueamos
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
      const menuElement = document.querySelector('.MuiMenu-paper, .MuiPopover-paper');
      if (menuElement) {
        return;
      }
      return;
    }
    setActiveStep(0);
    setSelectedListId(null);
    setFormData({ name: '', description: '' });
    setSelectedClients([]);
    onClose();
  };

  // Columnas para la tabla de listas (Paso 1)
  const listColumns = [
    {
      field: 'radio',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <Radio
          checked={selectedListId === String(params.row.id)}
          onChange={() => {
            const listId = params.row.id;
            console.log('[CreateListModal] List radio selected:', listId);
            setSelectedListId(String(listId));
          }}
          value={String(params.row.id)}
          size="small"
        />
      )
    },
    {
      field: 'name',
      headerName: t('name') || 'Nombre',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.isSystem && (
            <Chip label="Sistema" size="small" color="info" variant="outlined" />
          )}
          {params.row.isStartFromScratch && (
            <Chip label="Nuevo" size="small" color="default" variant="outlined" />
          )}
        </Box>
      )
    },
    {
      field: 'description',
      headerName: t('description') || 'Descripción',
      flex: 3
    }
  ];

  // Columnas para la tabla de clientes (Paso 2)
  const clientColumns = [
    {
      field: 'clientType',
      headerName: t('type') || 'Tipo',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value === 'B2B' ? 'primary' : 'secondary'} 
        />
      )
    },
    {
      field: 'displayName',
      headerName: t('name') || 'Nombre',
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: t('email') || 'Email',
      flex: 2,
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'phone',
      headerName: t('phone') || 'Teléfono',
      flex: 1.5,
      renderCell: (params) => params.value || '-'
    }
  ];

  const steps = [
    t('list_details') || 'Detalles de la Lista',
    t('select_list') || 'Seleccionar Lista',
    t('add_clients') || 'Agregar Clientes'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('add')}
          {t('create_list')}
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            padding: 0,
            borderRadius: '50%',
            '&:hover': {
              borderRadius: '50%'
            }
          }}
        >
          {getIcon('close')}
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Paso 1: Detalles de la Lista */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('list_details') || 'Detalles de la Lista'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('enter_list_details') || 'Ingresa el nombre y descripción de la lista'}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('name') || 'Nombre'}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder={t('list_name_placeholder') || 'Nombre de la lista'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('description') || 'Descripción'}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('list_description_placeholder') || 'Descripción de la lista'}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 2: Seleccionar lista para clonar o empezar desde cero */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('select_list_to_clone') || 'Selecciona una lista para clonar o empezar desde cero'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('select_list_help') || 'Puedes clonar una lista del sistema o una personalizada, o empezar desde cero'}
            </Typography>

            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={allListOptions}
                columns={listColumns}
                getRowId={(row) => String(row.id || row.id)}
                onRowClick={(params) => {
                  const listId = String(params.row.id);
                  console.log('[CreateListModal] List row clicked:', listId);
                  setSelectedListId(listId);
                }}
                hideFooter
                disableRowSelectionOnClick={false}
              />
            </Box>
          </Box>
        )}

        {/* Paso 3: Agregar clientes */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('add_clients_to_list') || 'Agregar Clientes a la Lista'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('select_clients_help') || 'Selecciona los clientes que deseas agregar a esta lista. Puedes buscar y filtrar por tipo de cliente.'}
            </Typography>

            {/* Loading mientras se cargan los contacts de la lista */}
            {loadingListContacts ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 6,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('loading_list_clients') || 'Cargando clientes de la lista...'}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Sección 1: Clientes ya seleccionados */}
            <Paper 
              sx={{ 
                p: 2.5, 
                mb: 3,
                bgcolor: 'grey.50', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {t('selected_clients') || 'Clientes Seleccionados'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedClients.length} {selectedClients.length === 1 ? t('client') || 'cliente' : t('clients') || 'clientes'} {t('selected') || 'seleccionado(s)'}
                  </Typography>
                </Box>
                {selectedClientsFullData.length > 0 && (
                  <Chip 
                    label={selectedClientsFullData.length}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              
              {selectedClientsFullData.length > 0 ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1.5, 
                    maxHeight: 150, 
                    overflowY: 'auto', 
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: 60
                  }}
                >
                  {selectedClientsFullData.map((client) => (
                    <Chip
                      key={client.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {client.displayName || client.name || `Cliente ${client.identifier}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                            ({client.clientType})
                          </Typography>
                        </Box>
                      }
                      onDelete={() => handleRemoveClient(client.id)}
                      color={client.clientType === 'B2B' ? 'primary' : 'secondary'}
                      variant="filled"
                      size="medium"
                      sx={{ 
                        '& .MuiChip-deleteIcon': {
                          fontSize: '1.1rem',
                          color: 'inherit'
                        },
                        fontWeight: 'medium'
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('no_clients_selected_yet') || 'No hay clientes seleccionados aún. Selecciona clientes en la tabla de abajo.'}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Sección 2: Tabla para agregar/quitar clientes */}
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {t('select_clients_to_add') || 'Seleccionar Clientes para Agregar'}
                </Typography>
                
                {/* Filtros */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('search') || 'Buscar'}
                    variant="outlined"
                    size="small"
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    sx={{ width: 200 }}
                    placeholder={t('search_clients') || 'Buscar por nombre, email, etc.'}
                  />
                  
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t('client_type') || 'Tipo de Cliente'}</InputLabel>
                    <Select
                      value={clientTypeFilter}
                      label={t('client_type') || 'Tipo de Cliente'}
                      onChange={(e) => setClientTypeFilter(e.target.value)}
                    >
                      <MenuItem value="all">{t('all') || 'Todos'}</MenuItem>
                      <MenuItem value="b2c">B2C</MenuItem>
                      <MenuItem value="b2b">B2B</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={displayClients}
                  columns={clientColumns}
                  getRowId={(row) => row.id}
                  checkboxSelection
                  onRowSelectionModelChange={handleClientSelectionChange}
                  rowSelectionModel={selectedClients}
                  paginationModel={clientPaginationModel}
                  onPaginationModelChange={setClientPaginationModel}
                  pageSizeOptions={[10, 25, 50]}
                  loading={clientLoading}
                  disableRowSelectionOnClick={false}
                  rowCount={clientTotalRows}
                  paginationMode="server"
                />
              </Box>
            </Paper>
              </>
            )}
          </Box>
        )}

      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {t('cancel')}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined">
            {t('back')}
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={
              loadingListContacts || 
              (activeStep === 0 && (!formData.name.trim() || !formData.description.trim()))
            }
          >
            {t('next')}
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name.trim() || selectedClientsFullData.length === 0 || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : getIcon('checkmarkDone')}
          >
            {saving ? (t('saving') || 'Guardando...') : t('create')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateListModal;
